import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShieldCheck, Copy, Printer, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { escapeHtml, openPrintWindow } from '../lib/printUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Dialog mostrato dopo login utente se backend ritorna suggest_2fa: true.
 * Permette di: attivare 2FA con QR code, oppure saltare (e non rivedere il prompt).
 */
const User2FAPrompt = ({ open, onClose, userEmail }) => {
  const [step, setStep] = useState('intro'); // intro | setup | recovery
  const [qrData, setQrData] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auth via cookie httpOnly automatico (axios.defaults.withCredentials=true)

  const handleAttiva = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/auth/2fa/start-optional-setup`, {});
      setQrData(data.qr_data_url);
      setSecret(data.secret);
      setStep('setup');
    } catch (e) { setError(e.response?.data?.detail || 'Errore'); }
    finally { setLoading(false); }
  };

  const handleConferma = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/auth/2fa/confirm-optional-setup`, { code });
      setRecoveryCodes(data.recovery_codes);
      setStep('recovery');
    } catch (err) { setError(err.response?.data?.detail || 'Codice non valido'); }
    finally { setLoading(false); }
  };

  const handleSalta = async () => {
    try {
      await axios.post(`${API_URL}/auth/2fa/skip-suggestion`, {});
    } catch (e) { console.warn('skip suggestion:', e); }
    onClose();
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Codici copiati');
  };

  const printCodes = () => {
    const dateStr = new Date().toLocaleString('it-IT');
    const codesHtml = recoveryCodes.map(c => `<div class="code">${escapeHtml(c)}</div>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Codici di recupero 2FA</title>
<style>
  body { font-family: Arial, sans-serif; max-width: 700px; margin: 40px auto; padding: 20px; color: #1e293b; }
  h1 { color: #1e40af; margin-bottom: 8px; }
  .warning { background:#fef3c7; border:1px solid #fbbf24; border-radius:6px; padding:12px; margin:20px 0; font-size:13px; color:#78350f; }
  .codes { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin: 24px 0; }
  .code { font-family: 'Courier New', monospace; font-size: 22px; font-weight: bold; text-align:center; padding: 12px; background:#f1f5f9; border:1px solid #cbd5e1; border-radius:6px; letter-spacing:2px; }
  .meta { font-size: 12px; color: #475569; margin-bottom: 16px; }
</style></head><body>
<h1>Codici di recupero 2FA</h1>
<div style="color:#64748b;">Semplificaffitto.it &middot; by EDILSTUDIO s.r.l.</div>
<div class="meta"><strong>Account:</strong> ${escapeHtml(userEmail)}<br/><strong>Data:</strong> ${escapeHtml(dateStr)}</div>
<div class="warning"><strong>ATTENZIONE</strong> &mdash; Conserva questi 10 codici in un luogo sicuro. Ogni codice può essere usato <strong>una sola volta</strong>.</div>
<div class="codes">${codesHtml}</div>
<script>window.onload = function() { setTimeout(function() { window.print(); }, 300); };</script>
</body></html>`;
    const w = openPrintWindow(html);
    if (!w) { toast.error('Popup bloccato'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && step !== 'recovery' && onClose()}>
      <DialogContent className="max-w-md" data-testid="user-2fa-prompt">
        {step === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600"/>Proteggi il tuo account</DialogTitle>
              <DialogDescription>
                Ti consigliamo di attivare l'autenticazione a due fattori (2FA) per maggior sicurezza del tuo account.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-slate-700 space-y-2">
              <p><strong>Cosa serve:</strong> uno smartphone con un'app authenticator (Google Authenticator, Authy, 1Password).</p>
              <p><strong>Come funziona:</strong> ad ogni accesso, oltre alla password, inserisci un codice a 6 cifre che cambia ogni 30s.</p>
              <p><strong>Tempo:</strong> 2 minuti.</p>
            </div>
            {error && <p className="text-red-600 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{error}</p>}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={onClose} data-testid="2fa-skip-btn">Accedi ora, deciderò al prossimo login</Button>
              <Button onClick={handleAttiva} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="2fa-activate-btn">
                {loading ? 'Avvio...' : 'Attiva 2FA'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'setup' && (
          <>
            <DialogHeader>
              <DialogTitle>Configura l'app authenticator</DialogTitle>
              <DialogDescription>Scansiona il QR code o inserisci il segreto manualmente</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center bg-white border rounded-md p-3">
              {qrData && <img src={qrData} alt="QR Code 2FA" className="w-44 h-44" data-testid="user-2fa-qr"/>}
              <code className="text-xs bg-slate-100 px-2 py-1 rounded mt-2 select-all">{secret}</code>
            </div>
            <form onSubmit={handleConferma} className="space-y-3">
              <div>
                <Label>Codice dall'app (6 cifre)</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6} value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-xl tracking-widest" autoFocus required
                  data-testid="user-2fa-code"
                />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                <Button type="submit" disabled={loading || code.length !== 6} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="user-2fa-confirm">
                  {loading ? 'Verifica...' : 'Conferma e attiva'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}

        {step === 'recovery' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-green-700 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5"/>2FA attivato!
              </DialogTitle>
              <DialogDescription>Salva questi 10 codici di recupero: ti serviranno se perdi il telefono.</DialogDescription>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
              <strong>IMPORTANTE</strong>: dopo aver chiuso questa finestra non potrai più vedere i codici.
            </div>
            <div className="grid grid-cols-2 gap-2 bg-slate-50 border rounded-md p-3 font-mono text-sm">
              {recoveryCodes.map((c) => <div key={c} className="text-center py-1 bg-white border rounded">{c}</div>)}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={copyCodes}><Copy className="w-4 h-4 mr-2"/>Copia</Button>
              <Button variant="outline" onClick={printCodes}><Printer className="w-4 h-4 mr-2"/>Stampa</Button>
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="w-full bg-slate-900 hover:bg-slate-800">Ho salvato i codici</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default User2FAPrompt;
